import getConfig from 'next/config';
import {
  browserLocalPersistence,
  browserPopupRedirectResolver,
  getAuth,
  initializeAuth,
  setPersistence,
} from '@firebase/auth';
import { Firestore, getFirestore } from '@firebase/firestore';
import { FirebaseApp, getApp, initializeApp } from '@firebase/app';
import { isInWebView } from '@utils/mobile';
import logger from '@utils/logger';

class FirebaseService {
  private database: Firestore | undefined;
  public app: FirebaseApp | undefined;

  constructor(firebaseConfig: any) {
    try {
      this.app = getApp();
    } catch (error) {
      if (typeof window !== 'undefined') {
        this.app = initializeApp(firebaseConfig);
      }
    }

    if (this.app) {
      // In the native app WebView, Firebase Auth's default IndexedDB
      // persistence is harmful: the app opens several same-origin WebViews and
      // their IDB connections race, so a persistence read throws
      // "InvalidStateError: the database connection is closing" during the auth
      // bootstrap. That error is async, so onIdTokenChanged never fires its
      // first callback and every auth-gated page stays blank.
      //
      // Force localStorage persistence instead: it avoids IndexedDB entirely
      // (no InvalidStateError) yet, unlike inMemory, it survives the
      // full-document navigations the checkout does (delivery -> payment ->
      // confirmation), so auth stays continuous. We deliberately pass neither
      // indexedDBLocalPersistence (Firebase would read it to migrate state,
      // reintroducing the InvalidStateError) nor inMemoryPersistence (it does
      // not survive navigation, which is what broke checkout). We also pass the
      // popupRedirectResolver that getAuth() would auto-inject, otherwise
      // social sign-in (signInWithPopup) breaks in the WebView.
      // Browsers are unaffected and keep the default persistence.
      if (isInWebView()) {
        try {
          initializeAuth(this.app, {
            persistence: browserLocalPersistence,
            popupRedirectResolver: browserPopupRedirectResolver,
          });
        } catch {
          // Auth already initialized (import ordering): switch it anyway.
          void setPersistence(getAuth(this.app), browserLocalPersistence).catch(
            (error) => {
              logger.error('WebView auth persistence fallback failed', {
                error: String(error),
              });
            },
          );
        }
      }

      this.database = getFirestore(this.app);
    }
  }

  public get db() {
    if (!this.database) {
      throw new Error('Firebase database not initialized');
    }

    return this.database;
  }
}

const {
  publicRuntimeConfig: { firebaseConfig },
} = getConfig();

export default new FirebaseService(firebaseConfig);
